import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../../auth/authStore'
import { getPlanningRating } from '../../../shared/api/planning.api'
import type { PlanningRatingRow } from '@crm/shared'

/**
 * Один блок «Рейтинг план/факт» в PDF-отчёте. Отличается от
 * [PlanFactRating.tsx] на дашборде тем, что принимает явный period+date,
 * а не вычисляет «текущий месяц/квартал/год» сам — иначе нельзя было бы
 * выгрузить отчёт за прошедший период.
 *
 * Разметка повторяет дашбордную — html2canvas снимает один к одному.
 */

export interface RatingReportSectionProps {
  period: 'month' | 'quarter' | 'year' | 'custom'
  date?: string
  from?: string
  to?: string
  kind: 'financial' | 'akb'
}

function getSelection(user: { gridSettings?: Record<string, unknown> | null } | null): string[] {
  const gs = user?.gridSettings as Record<string, unknown> | null | undefined
  const sel = gs?.planningSelection
  if (Array.isArray(sel)) return sel.filter((x): x is string => typeof x === 'string')
  return []
}

function formatMoney(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000_000).toFixed(2)}M`
  return `${n}`
}

function percentColor(pct: number): { bar: string; text: string } {
  if (pct >= 90) return { bar: '#16a34a', text: '#059669' }
  if (pct >= 70) return { bar: '#2563eb', text: '#2563eb' }
  if (pct >= 45) return { bar: '#ea580c', text: '#ea580c' }
  return { bar: '#dc2626', text: '#dc2626' }
}

export function RatingReportSection({ period, date, from, to, kind }: RatingReportSectionProps) {
  const user = useAuthStore((s) => s.user)
  const selection = useMemo(() => getSelection(user), [user])

  const query = useMemo(() => {
    if (period === 'custom') {
      if (!from || !to) return null
      return { period: 'custom' as const, from, to }
    }
    if (!date) return null
    return { period, date }
  }, [period, date, from, to])

  const { data, isLoading } = useQuery({
    queryKey: ['planning-rating-report', query, selection],
    queryFn: () => getPlanningRating({ ...query!, managerIds: selection }),
    enabled: Boolean(query),
  })

  const title = kind === 'financial' ? 'Рейтинг план / факт' : 'Рейтинг АКБ план / факт'

  const rows = useMemo(() => {
    if (!data) return []
    const pickPlan = (r: PlanningRatingRow) =>
      kind === 'financial' ? r.planAmount : r.akbPlanCount
    const pickFact = (r: PlanningRatingRow) =>
      kind === 'financial' ? r.factAmount : r.akbFactCount
    return [...data.rows]
      .map((r) => {
        const plan = pickPlan(r)
        const fact = pickFact(r)
        const pct = plan > 0 ? Math.round((fact / plan) * 100) : -1
        return { row: r, plan, fact, pct }
      })
      .sort((a, b) => b.pct - a.pct)
  }, [data, kind])

  const withPlan = rows.filter((r) => r.pct >= 0)
  const noPlan = rows.filter((r) => r.pct < 0)

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 16,
        padding: 16,
        marginBottom: 12,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{title}</div>
        {withPlan.length > 0 && (
          <div style={{ fontSize: 11, color: '#9ca3af' }}>
            {withPlan.length} с планом, по % выполнения
          </div>
        )}
      </div>

      {isLoading || !data ? (
        <div style={{ fontSize: 13, color: '#6b7280', textAlign: 'center', padding: '24px 0' }}>
          Загрузка…
        </div>
      ) : withPlan.length === 0 ? (
        <div style={{ fontSize: 13, color: '#6b7280', textAlign: 'center', padding: '24px 0' }}>
          Ни у кого нет плана за выбранный период.
        </div>
      ) : (
        <div>
          {withPlan.map((item, idx) => {
            const color = percentColor(item.pct)
            const fill = Math.min(item.pct, 100)
            const fmt = (n: number) => (kind === 'financial' ? formatMoney(n) : String(n))
            return (
              <div
                key={item.row.unitId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '6px 0',
                  borderBottom: '1px solid #f3f4f6',
                  fontSize: 12,
                }}
              >
                <span
                  style={{
                    width: 20,
                    color: '#9ca3af',
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {idx + 1}
                </span>
                <span
                  style={{
                    width: 230,
                    flexShrink: 0,
                    color: '#1f2937',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.25,
                  }}
                >
                  {item.row.label}
                </span>
                <div
                  style={{
                    flex: 1,
                    height: 18,
                    background: '#f3f4f6',
                    borderRadius: 6,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${fill}%`,
                      background: color.bar,
                      borderRadius: 6,
                    }}
                  />
                </div>
                <span
                  style={{
                    width: 110,
                    fontSize: 11,
                    color: '#6b7280',
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {fmt(item.plan)} / {fmt(item.fact)}
                </span>
                <span
                  style={{
                    width: 50,
                    fontWeight: 600,
                    color: color.text,
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {item.pct}%
                </span>
              </div>
            )
          })}
          {noPlan.length > 0 && (
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
              Без плана: {noPlan.map((r) => r.row.label).join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
